import type { Disposable } from '@/shared/types/core'

/** glTF extension hook 所需的最小 accessor 描述，不依赖具体资产插件。 */
export interface GltfExtensionAccessor {
  readonly count: number
  readonly type?: string
}

/** glTF extension hook 所需的最小 primitive 描述。 */
export interface GltfExtensionPrimitive {
  readonly attributes: Readonly<Record<string, number>>
  readonly mode?: number
  readonly extensions?: Readonly<Record<string, unknown>>
}

/** glTF extension hook 所需的最小 mesh 描述。 */
export interface GltfExtensionMesh {
  readonly primitives: readonly GltfExtensionPrimitive[]
}

/** glTF extension hook 所需的最小 document 描述。 */
export interface GltfExtensionDocument {
  readonly meshes?: readonly GltfExtensionMesh[]
  readonly accessors?: readonly GltfExtensionAccessor[]
}

/** 已展开为紧密 Float32Array 的 accessor。 */
export interface GltfExtensionAccessorData {
  readonly count: number
  readonly componentCount: number
  readonly data: Float32Array
}

/** Loader 向 extension hook 注入的 accessor 读取边界。 */
export interface GltfExtensionAccessorReader {
  read(accessorIndex: number, signal?: AbortSignal): Promise<GltfExtensionAccessorData>
}

export type GltfExtensionDiagnosticLevel = 'warning' | 'error'

export interface GltfExtensionDiagnostic {
  readonly level: GltfExtensionDiagnosticLevel
  readonly code: string
  readonly message: string
}

/** 单个 extension 的容器结果；resourcesByMesh 必须与 glTF meshes 对齐。 */
export interface GltfExtensionResult extends Disposable {
  readonly extensionName: string
  /** 本次 document 中由该 hook 接管的 primitive 数量，包括诊断后跳过的无效项。 */
  readonly handledPrimitiveCount: number
  readonly resourcesByMesh: readonly (readonly Disposable[] | null)[]
  readonly diagnostics: readonly GltfExtensionDiagnostic[]
}

export interface GltfExtensionHookContext {
  readonly document: GltfExtensionDocument
  readonly accessorReader: GltfExtensionAccessorReader
  readonly signal?: AbortSignal
}

/** 资产插件可注入、而 glTF 容器 Loader 可消费的最小扩展 hook。 */
export interface GltfExtensionHook {
  readonly extensionName: string
  hasPrimitive(primitive: GltfExtensionPrimitive): boolean
  createResources(context: GltfExtensionHookContext): Promise<GltfExtensionResult>
}

/**
 * 引擎实例级 glTF extension registry。
 *
 * Registry 只持有 hook，不依赖 Mesh 或 GSplat；重复 extension 使用栈语义，便于插件销毁时
 * 精确恢复先前注册者。一次 process 调用失败会释放此前全部 staged 结果。
 */
export class GltfExtensionRegistry implements Disposable {
  private readonly hooks = new Map<string, GltfExtensionHook[]>()
  private disposed = false

  register(hook: GltfExtensionHook): Disposable {
    this.assertActive()
    const extensionName = hook.extensionName.trim()
    if (!extensionName) throw new Error('glTF extension name 不能为空')
    const stack = this.hooks.get(extensionName) ?? []
    stack.push(hook)
    this.hooks.set(extensionName, stack)

    let released = false
    return {
      dispose: () => {
        if (released) return
        released = true
        const current = this.hooks.get(extensionName)
        if (!current) return
        const index = current.lastIndexOf(hook)
        if (index >= 0) current.splice(index, 1)
        if (current.length === 0) this.hooks.delete(extensionName)
      },
    }
  }

  get extensionNames(): readonly string[] {
    return Object.freeze(Array.from(this.hooks.keys()))
  }

  handlesPrimitive(primitive: GltfExtensionPrimitive): boolean {
    this.assertActive()
    for (const stack of this.hooks.values()) {
      const hook = stack.at(-1)
      if (hook?.hasPrimitive(primitive)) return true
    }
    return false
  }

  async process(
    context: GltfExtensionHookContext
  ): Promise<ReadonlyMap<string, GltfExtensionResult>> {
    this.assertActive()
    const staged = new Map<string, GltfExtensionResult>()
    try {
      for (const [extensionName, stack] of this.hooks) {
        const hook = stack.at(-1)
        if (!hook) continue
        const result = await hook.createResources(context)
        if (result.extensionName !== extensionName) {
          result.dispose()
          throw new Error(
            `glTF extension hook 返回了错误的名称: expected=${extensionName}, actual=${result.extensionName}`
          )
        }
        staged.set(extensionName, result)
      }
      return staged
    } catch (error) {
      for (const result of Array.from(staged.values()).reverse()) {
        try {
          result.dispose()
        } catch {
          // 保留原始 extension 处理错误。
        }
      }
      throw error
    }
  }

  dispose(): void {
    if (this.disposed) return
    this.disposed = true
    this.hooks.clear()
  }

  private assertActive(): void {
    if (this.disposed) throw new Error('GltfExtensionRegistry 已释放')
  }
}
